import {
  Avatar,
  ClickAwayListener,
  Divider as MuiDivider,
  List,
  ListItem,
  Paper,
  Popover as MuiPopover,
  PopoverOrigin,
  Tooltip,
  TooltipProps,
  styled,
} from '@mui/material'
import AccessAlarmIcon from '@mui/icons-material/AccessAlarm'
import { memo, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { intl } from '../../../../../i18n'
import {
  DescendantRow,
  ReferencedValueSnapshot,
  WbsItemAggregateCategory,
} from '../../../../../domain/value-object/ProjectPlanSnapshot'
import {
  WbsItemStatus,
  WbsItemType,
} from '../../../../../domain/entity/WbsItemEntity'
import { ProgressReportScopeType } from '../../../../../domain/entity/version-report/VersionReportVersionEntity'
import { WbsItemStatus as AgGridWbsItemStatus } from '../../../../containers/commons/AgGrid/components/cell/custom/wbsItemStatus'
import WbsItemStatusLabel from '../../../../components/labels/WbsItemStatusLabel'
import { useDeliverableTaskSnapshot } from '../../hooks/useDeliverableTaskSnapshot'
import { useSnapshotTeams } from '../../hooks/useSnapshotTeams'
import DateVO from '../../../../../vo/DateVO'
import { DISPLAY_DATE_FORMAT_WITH_DAY } from '../../../../../utils/date'
import { colorPalette } from '../../../../style/colorPalette'
import {
  BorderColor,
  FontWeight,
  TextColor,
} from '../../../../../styles/commonStyles'
import { AllState } from '../../../../../store'
import { BaseWbsItemType } from '../../../../../store/project'
import { useProjectPrivateContext } from '../../../../context/projectContext'
import { WbsItemTypeVO } from '../../../../../domain/value-object/WbsItemTypeVO'

const STATUS_MAP: Record<WbsItemStatus, AgGridWbsItemStatus> = {
  [WbsItemStatus.TODO]: AgGridWbsItemStatus.TODO,
  [WbsItemStatus.DOING]: AgGridWbsItemStatus.DOING,
  [WbsItemStatus.REVIEW]: AgGridWbsItemStatus.REVIEW,
  [WbsItemStatus.DONE]: AgGridWbsItemStatus.DONE,
  [WbsItemStatus.DISCARD]: AgGridWbsItemStatus.DISCARD,
}

const HEADER_HEIGHT = 32
const FOOTER_HEIGHT = 32

type DescendantRowItemProps = {
  row: DescendantRow
  index: number
  teamMap: Map<string, ReferencedValueSnapshot>
  wbsItemTypes: BaseWbsItemType
  ticketTypes: WbsItemTypeVO[]
}

const tooltipCommonProps: Pick<
  TooltipProps,
  'arrow' | 'enterDelay' | 'placement' | 'componentsProps'
> = {
  arrow: true,
  enterDelay: 500,
  placement: 'bottom-start',
  componentsProps: {
    tooltip: { sx: { fontSize: '12px' } },
  },
}

const DescendantRowItem = memo(
  ({
    row,
    index,
    teamMap,
    wbsItemTypes,
    ticketTypes,
  }: DescendantRowItemProps) => {
    const ticketType = row.wbsItem.ticketType
      ? ticketTypes.find(t => t.code === row.wbsItem.ticketType)
      : undefined
    const displayType = ticketType ?? wbsItemTypes.get(row.type)
    const team = row.wbsItem.teamUuid
      ? teamMap.get(row.wbsItem.teamUuid)
      : undefined
    const endDate = row.wbsItem.scheduledDate?.endDate
      ? new DateVO(row.wbsItem.scheduledDate.endDate).format(
          DISPLAY_DATE_FORMAT_WITH_DAY
        )
      : undefined
    const pathText = row.path.join(' / ')
    return (
      <>
        {index !== 0 && <RowDivider />}
        <DescendantListItem>
          <LeftColumn>
            <StatusArea>
              <WbsItemStatusLabel
                status={STATUS_MAP[row.wbsItem.status]}
                readonly={true}
              />
            </StatusArea>
            <NameLine>
              {displayType?.iconUrl && (
                <Tooltip title={displayType.name} {...tooltipCommonProps}>
                  <TypeIcon src={displayType.iconUrl} alt={displayType.name} />
                </Tooltip>
              )}
              <Tooltip title={row.wbsItem.displayName} {...tooltipCommonProps}>
                <RowName>{row.wbsItem.displayName}</RowName>
              </Tooltip>
            </NameLine>
            {pathText && (
              <Tooltip title={pathText} {...tooltipCommonProps}>
                <PathText>{pathText}</PathText>
              </Tooltip>
            )}
          </LeftColumn>
          <RightColumn>
            <TopRight>
              {endDate && (
                <EndDate>
                  <EndDateIcon />
                  {endDate}
                </EndDate>
              )}
            </TopRight>
            <TeamInfoBox>
              {team?.iconUrl && (
                <TeamAvatar variant="circular" src={team.iconUrl} />
              )}
              {team && (
                <Tooltip title={team.name} {...tooltipCommonProps}>
                  <TeamName>{team.name}</TeamName>
                </Tooltip>
              )}
            </TeamInfoBox>
          </RightColumn>
        </DescendantListItem>
      </>
    )
  }
)
DescendantRowItem.displayName = 'DescendantRowItem'

type DeliverableTaskPopperProps = {
  anchorEl: Element | null
  snapshotUuid: string
  rootUuid: string | undefined
  /** 未指定時は工程配下（PROCESS）として扱う */
  scopeType?: ProgressReportScopeType
  aggregateType?: WbsItemAggregateCategory
  aggregateTarget?: WbsItemType
  teamUuid?: string
  clickPosition?: { x: number; y: number }
  onClose: () => void
}

export const DeliverableTaskPopper = ({
  anchorEl,
  snapshotUuid,
  rootUuid,
  scopeType = 'PROCESS',
  aggregateType,
  aggregateTarget,
  teamUuid,
  clickPosition,
  onClose,
}: DeliverableTaskPopperProps) => {
  const open = Boolean(anchorEl) && !!rootUuid
  const { fetch, loading } = useDeliverableTaskSnapshot(snapshotUuid)
  const teamMap = useSnapshotTeams(snapshotUuid)
  const wbsItemTypes = useSelector<AllState, BaseWbsItemType>(
    state => state.project.wbsItemTypes
  )
  const { ticketTypes } = useProjectPrivateContext()
  const [rows, setRows] = useState<DescendantRow[]>([])

  useEffect(() => {
    if (!open || !rootUuid) {
      setRows([])
      return
    }
    const token = { cancelled: false }
    fetch(rootUuid, scopeType)
      .then(result => {
        if (token.cancelled) return
        const filtered = result
          .filter(r => !aggregateTarget || r.type === aggregateTarget)
          .filter(r => !aggregateType || r.categories.includes(aggregateType))
          .filter(r => !teamUuid || r.wbsItem.teamUuid === teamUuid)
        setRows(filtered)
      })
      .catch(error => {
        // 失敗時に前回のセルの内訳が残ると別セルの中身を見ていると誤認させるため空にする。
        // ユーザーへの通知は共通API層(lib/commons/api)が行う
        console.error('Failed to fetch drill down rows:', error)
        if (token.cancelled) return
        setRows([])
      })
    return () => {
      token.cancelled = true
    }
  }, [
    open,
    rootUuid,
    scopeType,
    aggregateType,
    aggregateTarget,
    teamUuid,
    fetch,
  ])

  const { anchorOrigin, transformOrigin } = useMemo(() => {
    const ao: PopoverOrigin = { vertical: 'bottom', horizontal: 'right' }
    const to: PopoverOrigin = { vertical: 'top', horizontal: 'left' }
    if (clickPosition && clickPosition.x > window.innerWidth / 2) {
      ao.horizontal = 'left'
      to.horizontal = 'right'
    }
    if (clickPosition && clickPosition.y > window.innerHeight / 2) {
      ao.vertical = 'top'
      to.vertical = 'bottom'
    }
    return { anchorOrigin: ao, transformOrigin: to }
  }, [clickPosition])

  return (
    <MuiPopover
      open={open}
      anchorEl={anchorEl}
      anchorOrigin={anchorOrigin}
      transformOrigin={transformOrigin}
      onClose={onClose}
    >
      <ClickAwayListener onClickAway={onClose}>
        <DialogPaper>
          <HeaderContainer>
            <HeaderTitle>
              {intl.formatMessage({ id: 'progressReport.popper.title' })}
            </HeaderTitle>
          </HeaderContainer>
          <ListContainer>
            {!loading && rows.length === 0 && (
              <EmptyText>
                {intl.formatMessage({ id: 'progressReport.popper.empty' })}
              </EmptyText>
            )}
            {rows.map((row, index) => (
              <DescendantRowItem
                key={row.uuid}
                row={row}
                index={index}
                teamMap={teamMap}
                wbsItemTypes={wbsItemTypes}
                ticketTypes={ticketTypes}
              />
            ))}
          </ListContainer>
          <LastDivider />
          <FooterContainer>
            <CountText>
              {intl.formatMessage(
                { id: 'progressReport.popper.count' },
                { count: rows.length }
              )}
            </CountText>
          </FooterContainer>
        </DialogPaper>
      </ClickAwayListener>
    </MuiPopover>
  )
}

// ============================================
// Styled components
// ============================================

const DialogPaper = styled(Paper)({
  display: 'flex',
  flexDirection: 'column',
  maxHeight: 'min(400px, calc(100vh - 48px))',
  width: '422px',
})

const HeaderContainer = styled('div')({
  height: HEADER_HEIGHT,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  color: TextColor.WHITE,
  backgroundColor: TextColor.GREY,
  borderRadius: '5px 5px 0 0',
  flexShrink: 0,
})

const HeaderTitle = styled('span')({
  marginLeft: '10px',
  fontSize: '12px',
  fontWeight: FontWeight.BOLD,
})

const ListContainer = styled(List)({
  padding: '0 8px',
  overflowX: 'hidden',
  overflowY: 'auto',
  flex: '1 1 auto',
  minHeight: 0,
  scrollbarGutter: 'stable',
}) as typeof List

const DescendantListItem = styled(ListItem)({
  minHeight: '72px',
  padding: '8px 10px',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'stretch',
  gap: '12px',
})

const RowDivider = styled(MuiDivider)({
  borderTop: `1px dotted ${BorderColor.GREY}`,
  background: 'transparent',
})

const LeftColumn = styled('div')({
  flex: '1 1 auto',
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
})

const RightColumn = styled('div')({
  flexShrink: 0,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  alignItems: 'flex-end',
  gap: '8px',
})

const TopRight = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
})

const NameLine = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  minWidth: 0,
})

const TypeIcon = styled('img')({
  width: 18,
  height: 18,
  flexShrink: 0,
})

const StatusArea = styled('div')({
  display: 'inline-block',
  flexShrink: 0,
  minWidth: '56px',
})

const RowName = styled('span')({
  fontSize: '12px',
  fontWeight: FontWeight.BOLD,
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
  minWidth: 0,
})

const TeamInfoBox = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  flexShrink: 0,
  maxWidth: '160px',
})

const TeamAvatar = styled(Avatar)({
  width: 18,
  height: 18,
  flexShrink: 0,
})

const TeamName = styled('span')({
  fontSize: '11px',
  fontWeight: FontWeight.BOLD,
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
})

const EndDate = styled('span')({
  display: 'flex',
  alignItems: 'center',
  fontSize: '11px',
  color: colorPalette.monotone[6],
  flexShrink: 0,
})

const EndDateIcon = styled(AccessAlarmIcon)({
  fontSize: '13pt',
  marginRight: '2px',
})

const PathText = styled('span')({
  fontSize: '11px',
  color: colorPalette.monotone[6],
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
  minWidth: 0,
})

const EmptyText = styled('span')({
  display: 'block',
  padding: '16px',
  fontSize: '12px',
  color: colorPalette.monotone[5],
  textAlign: 'center',
})

const LastDivider = styled(MuiDivider)({
  borderTop: `1px solid ${BorderColor.GREY}`,
  background: 'transparent',
  margin: '0 10px',
})

const FooterContainer = styled('div')({
  padding: '0 14px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  height: FOOTER_HEIGHT,
  flexShrink: 0,
})

const CountText = styled('span')({
  fontSize: '11px',
  fontWeight: FontWeight.BOLD,
  color: colorPalette.monotone[7],
})
